const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

// Endings GEDCOM has no tag for, written as `1 EVEN` / `2 TYPE <text>`. The
// importer matches on these words, so keep them and the matcher in step.
const MARRIAGE_END_TYPES = {
  death: 'Marriage ended by death of spouse',
  separation: 'Legal separation',
  unknown: 'Marriage ended',
};

// Marks a person's own timeline entries, and the photo pointer. Both are read
// back by importGedcom.js — keep the strings identical at both ends.
export const LIFE_EVENT_TYPE = 'Life event';
export const PHOTO_NOTE_PREFIX = 'Photo: ';

/**
 * A NOTE (or any free-text value) as GEDCOM lines.
 *
 * A line break inside a value is not legal in GEDCOM: the text has to be split
 * across CONT lines, or every parser reading the file — including this app's —
 * sees a truncated note followed by lines it cannot make sense of. Notes are a
 * free-text field, so they contain newlines routinely.
 */
function noteLines(level, text) {
  const [first, ...rest] = String(text).split(/\r\n|\r|\n/);
  return [
    `${level} NOTE ${first}`,
    ...rest.map(line => `${level + 1} CONT ${line}`),
  ];
}

// Partial dates are ordinary in genealogy — a year with no month is a complete
// answer, not a truncated one — and both the date fields and the GEDCOM DATE
// grammar accept them. This used to require a month and returned null without
// one, so a person recorded as born in "1950" exported with no birth date at
// all; importing a file full of year-only dates made that routine.
function gedDate(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-');
  if (!/^\d{3,4}$/.test(y || '')) return null;

  const parts = [];
  const month = m ? MONTHS[parseInt(m, 10) - 1] : null;
  if (m && !month) return null; // a month that isn't one means a malformed date
  if (month) {
    if (d && parseInt(d, 10) > 0) parts.push(parseInt(d, 10));
    parts.push(month);
  }
  parts.push(y);
  return parts.join(' ');
}

export function exportGedcom(people, treeName) {
  const lines = [];
  const personList = Object.values(people);

  // Stable xref IDs for each person
  const indiId = {};
  personList.forEach((p, i) => { indiId[p.id] = `I${i + 1}`; });

  // GEDCOM can only express parentage through a FAM record, so the families
  // have to be reconstructed from an app that has no family entity.
  //
  // This used to take a FAM's children from `marriage.children` alone, which
  // meant it exported almost no parent-child links at all: every path in the
  // UI that adds a child — AddPersonModal, AddRelatedPersonModal,
  // EditRelationshipsModal — records it in the child's `parentIds` and the
  // parent's `children`, and creates marriages with `children: []`. The canvas
  // builds its family units from `parentIds` for that reason, and this is now
  // consistent with it: a tree exported before this fix has its spouse links
  // (FAMS) but no CHIL or FAMC lines whatsoever.
  //
  // All three places a relationship can be recorded are read, because a tree
  // edited over time can hold any mix of them and disagreeing halves should
  // widen the result rather than silently drop it.
  const parentsOf = new Map(); // childId → Set of parentId

  const addEdge = (childId, parentId) => {
    if (!childId || !parentId || childId === parentId) return;
    if (!people[childId] || !people[parentId]) return; // dangling reference
    if (!parentsOf.has(childId)) parentsOf.set(childId, new Set());
    parentsOf.get(childId).add(parentId);
  };

  for (const person of personList) {
    for (const parentId of (person.parentIds || [])) addEdge(person.id, parentId);
    for (const childId of (person.children || [])) addEdge(childId, person.id);
    for (const m of (person.marriages || [])) {
      // A child on a marriage belongs to both spouses.
      for (const childId of (m.children || [])) {
        addEdge(childId, person.id);
        addEdge(childId, m.spouseId);
      }
    }
  }

  // FAM records, keyed by canonical "minId|maxId" for a couple or "solo|id" for
  // a lone parent. Marriages are mirrored on both spouses, so the key also
  // deduplicates the two copies of the same marriage.
  const famMap = new Map(); // key → { husbId, wifeId, marriageDate, marriagePlace, children }
  const famKeyOrder = []; // preserve insertion order for stable xref numbering

  const coupleKey = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);

  const ensureFam = (key, husbId, wifeId) => {
    if (!famMap.has(key)) {
      famMap.set(key, {
        husbId, wifeId, marriageDate: null, marriagePlace: null, children: [],
      });
      famKeyOrder.push(key);
    }
    return famMap.get(key);
  };

  const addChild = (fam, childId) => {
    if (!fam.children.includes(childId)) fam.children.push(childId);
  };

  // Marriages first, so a couple's own FAM exists before children are placed
  // and their children land in the family that carries the marriage date.
  for (const person of personList) {
    for (const m of (person.marriages || [])) {
      if (!m.spouseId || !people[m.spouseId]) continue;
      const key = coupleKey(person.id, m.spouseId);
      if (famMap.has(key)) continue;

      const husbId = person.gender === 'F' ? m.spouseId : person.id;
      const wifeId = person.gender === 'F' ? person.id : m.spouseId;

      const fam = ensureFam(key, husbId, wifeId);
      fam.marriageDate = m.marriageDate || null;
      fam.marriagePlace = m.marriagePlace || null;
      // How the marriage ended, which used to be dropped: nothing here read
      // status, endReason or endDate, so a divorce recorded in the app came out
      // of the export as an ordinary marriage and came back the same way.
      fam.status = m.status || 'current';
      fam.endReason = m.endReason || null;
      fam.endDate = m.endDate || null;
    }
  }

  // Then place every child in a family per set of parents.
  for (const child of personList) {
    const parents = [...(parentsOf.get(child.id) || [])];
    if (!parents.length) continue;

    const unplaced = new Set(parents);

    // Two parents who already share a family — married, or paired for an
    // earlier child — keep that family rather than starting another.
    for (const a of parents) {
      for (const b of parents) {
        if (a >= b) continue;
        if (!unplaced.has(a) || !unplaced.has(b)) continue;
        const key = coupleKey(a, b);
        if (!famMap.has(key)) continue;
        addChild(famMap.get(key), child.id);
        unplaced.delete(a);
        unplaced.delete(b);
      }
    }

    const rest = [...unplaced];
    if (rest.length === 2) {
      // An unmarried couple. A FAM with no MARR is exactly how GEDCOM says
      // this, so it needs no marriage to hold the two of them together.
      const [a, b] = rest;
      const husbId = people[a].gender === 'F' ? b : a;
      const wifeId = people[a].gender === 'F' ? a : b;
      addChild(ensureFam(coupleKey(a, b), husbId, wifeId), child.id);
    } else {
      // One parent, or more than two (a birth and an adoptive set, say). Each
      // gets a single-parent FAM: pairing them off would assert a couple the
      // tree never recorded, and multiple FAMC links are how GEDCOM carries
      // more than one set of parents anyway.
      for (const parentId of rest) {
        const isFemale = people[parentId].gender === 'F';
        addChild(
          ensureFam(`solo|${parentId}`, isFemale ? null : parentId, isFemale ? parentId : null),
          child.id,
        );
      }
    }
  }

  const famId = {};
  famKeyOrder.forEach((key, i) => { famId[key] = `F${i + 1}`; });

  function spouseFamKeys(personId) {
    return famKeyOrder.filter(k => {
      const f = famMap.get(k);
      return f.husbId === personId || f.wifeId === personId;
    });
  }

  function childFamKeys(personId) {
    return famKeyOrder.filter(k => famMap.get(k).children.includes(personId));
  }

  // HEAD
  lines.push('0 HEAD');
  lines.push('1 SOUR KinshipTrees');
  lines.push('2 VERS 1.0');
  lines.push('2 NAME Kinship Trees');
  lines.push('1 GEDC');
  lines.push('2 VERS 5.5.1');
  lines.push('2 FORM LINEAGE-LINKED');
  lines.push('1 CHAR UTF-8');
  lines.push('1 SUBM @SUBM@');
  if (treeName) lines.push(`1 NOTE ${treeName}`);

  // SUBM record (required by GEDCOM 5.5.1)
  lines.push('0 @SUBM@ SUBM');
  lines.push(`1 NAME ${treeName || 'Kinship Trees'}`);

  // INDI records
  for (const person of personList) {
    const xref = indiId[person.id];
    lines.push(`0 @${xref}@ INDI`);
    if (person.name)       lines.push(`1 NAME ${person.name}`);
    if (person.gender)     lines.push(`1 SEX ${person.gender === 'F' ? 'F' : 'M'}`);

    const bDate = gedDate(person.birth);
    if (bDate || person.birthPlace) {
      lines.push('1 BIRT');
      if (bDate)            lines.push(`2 DATE ${bDate}`);
      if (person.birthPlace) lines.push(`2 PLAC ${person.birthPlace}`);
    }

    const dDate = gedDate(person.death);
    if (dDate || person.deathPlace) {
      lines.push('1 DEAT');
      if (dDate)             lines.push(`2 DATE ${dDate}`);
      if (person.deathPlace) lines.push(`2 PLAC ${person.deathPlace}`);
    }

    if (person.occupation) lines.push(`1 OCCU ${person.occupation}`);
    if (person.photoURL)   lines.push(`1 NOTE ${PHOTO_NOTE_PREFIX}${person.photoURL}`);

    // The person's own notes. These were never exported at all, so anything
    // typed into the notes field was lost the moment a tree left the app —
    // while the importer has always read NOTE back, which made the omission
    // invisible from the inside.
    if (person.notes) lines.push(...noteLines(1, person.notes));

    // Timeline entries the user added by hand. GEDCOM has no tag for "a thing
    // that happened", so they go out as custom events, the same device used for
    // marriage endings above. The colour is a display choice with nowhere to
    // live in the standard, hence the underscore tag — the convention for a
    // field a particular product owns.
    for (const event of (person.lifeEvents || [])) {
      if (!event || (!event.text && !event.year)) continue;
      lines.push('1 EVEN');
      lines.push(`2 TYPE ${LIFE_EVENT_TYPE}`);
      if (event.year)  lines.push(`2 DATE ${event.year}`);
      if (event.text)  lines.push(...noteLines(2, event.text));
      if (event.color) lines.push(`2 _COLOR ${event.color}`);
    }

    for (const k of spouseFamKeys(person.id)) lines.push(`1 FAMS @${famId[k]}@`);
    for (const k of childFamKeys(person.id))  lines.push(`1 FAMC @${famId[k]}@`);
  }

  // FAM records
  for (const key of famKeyOrder) {
    const fam = famMap.get(key);
    const xref = famId[key];
    lines.push(`0 @${xref}@ FAM`);
    if (fam.husbId && indiId[fam.husbId]) lines.push(`1 HUSB @${indiId[fam.husbId]}@`);
    if (fam.wifeId && indiId[fam.wifeId]) lines.push(`1 WIFE @${indiId[fam.wifeId]}@`);

    const mDate = gedDate(fam.marriageDate);
    if (mDate || fam.marriagePlace) {
      lines.push('1 MARR');
      if (mDate)             lines.push(`2 DATE ${mDate}`);
      if (fam.marriagePlace) lines.push(`2 PLAC ${fam.marriagePlace}`);
    }

    // How it ended. GEDCOM has tags for two of the four reasons this app
    // records, and nothing at all for the others — so the rest go out as a
    // custom event, which is the conventional way to carry something the
    // standard has no tag for. Any other program reads DIV and ANUL properly
    // and ignores the EVEN; this app's own importer understands all four, so a
    // round trip through here loses nothing.
    if (fam.status === 'ended') {
      const eDate = gedDate(fam.endDate);
      if (fam.endReason === 'divorce') {
        lines.push('1 DIV');
        if (eDate) lines.push(`2 DATE ${eDate}`);
      } else if (fam.endReason === 'annulment') {
        lines.push('1 ANUL');
        if (eDate) lines.push(`2 DATE ${eDate}`);
      } else {
        // A marriage ended by a spouse's death is already implied by their
        // DEAT record, and a legal separation has no tag whatsoever. Naming
        // the reason keeps it from being read back as a divorce.
        lines.push('1 EVEN');
        lines.push(`2 TYPE ${MARRIAGE_END_TYPES[fam.endReason] || MARRIAGE_END_TYPES.unknown}`);
        if (eDate) lines.push(`2 DATE ${eDate}`);
      }
    }

    for (const childId of fam.children) {
      if (indiId[childId]) lines.push(`1 CHIL @${indiId[childId]}@`);
    }
  }

  lines.push('0 TRLR');
  return lines.join('\r\n'); // GEDCOM spec requires CRLF
}

export function downloadGedcom(people, treeName) {
  const content = exportGedcom(people, treeName);
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (treeName || 'family-tree').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');
  a.href = url;
  a.download = `${safeName}.ged`;
  a.click();
  URL.revokeObjectURL(url);
}
